import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth } from '@/firebaseConfig';

// Enable only after the callable and the compatible client have been released.
// An absent flag keeps the existing installation operational during rollout.
const gatewayMode = process.env.VUE_APP_TRAINING_WRITE_GATEWAY;
const gatewayRegion = ['us-central1', 'europe-west1'].includes(process.env.VUE_APP_TRAINING_WRITE_GATEWAY_REGION)
  ? process.env.VUE_APP_TRAINING_WRITE_GATEWAY_REGION : 'us-central1';
const pilotUserId = process.env.VUE_APP_TRAINING_WRITE_GATEWAY_PILOT_USER_ID;
const pilotActorId = process.env.VUE_APP_TRAINING_WRITE_GATEWAY_PILOT_ACTOR_ID;
const pilotActions = new Set(['save', 'save-new', 'publish']);
export const trainingWriteGatewayEnabled = gatewayMode === 'server' || gatewayMode === 'pilot';
export const trainingWriteDiagnosticsEnabled = gatewayMode === 'pilot' && process.env.VUE_APP_TRAINING_WRITE_DIAGNOSTICS === 'true';

function directPilotBaseline() {
  // This switch compares transports only in the local pilot. Production and
  // global server mode never accept a query-string bypass.
  return gatewayMode === 'pilot' && process.env.NODE_ENV === 'development' && typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('trainingWriteTransport') === 'direct';
}

function performanceDiagnostics(value) {
  if (!value || typeof value !== 'object') return {};
  const result = {};
  for (const key of ['serverMs', 'writeValidationMs', 'transactionMs', 'transactionAttempts', 'callableMs',
    'readAuthorizationMs', 'readCurrentMs', 'readValidationMs']) {
    if (typeof value[key] === 'number' && Number.isFinite(value[key]) && value[key] >= 0) result[key] = value[key];
  }
  if (typeof value.firstInvocationInProcess === 'boolean') result.firstInvocationInProcess = value.firstInvocationInProcess;
  return result;
}

export async function writeTrainingWithGateway(action, payload) {
  if (!trainingWriteGatewayEnabled) return null;
  const user = auth.currentUser;
  if (gatewayMode === 'pilot' && (!pilotUserId || !pilotActorId || user?.uid !== pilotActorId
    || payload?.userId !== pilotUserId || !pilotActions.has(action) || directPilotBaseline())) return null;
  if (!user) throw Object.assign(new Error('Accedi nuovamente prima di modificare gli allenamenti.'), { code: 'training/unauthenticated' });
  const startedAt = trainingWriteDiagnosticsEnabled ? performance.now() : null;
  const request = { ...payload, action };
  delete request.diagnostics;
  if (trainingWriteDiagnosticsEnabled) request.diagnostics = true;
  let response;
  try {
    // The regional client belongs only to this gateway; other callable services
    // keep their configured instance and the absent flag performs no access.
    response = await httpsCallable(getFunctions(app, gatewayRegion), 'mutateTraining')(request);
  } catch (error) {
    // Never retry a failed callable through direct writes: it may already have committed.
    if (typeof error?.details?.trainingCode === 'string') {
      error.code = error.details.trainingCode;
    } else if (typeof error?.code === 'string' && error.code.startsWith('functions/')) {
      const code = error.code.slice('functions/'.length);
      error.code = code === 'aborted' ? 'training/conflict' : `training/${code}`;
      if (['internal', 'unavailable', 'deadline-exceeded', 'unknown', 'cancelled'].includes(code)) {
        error.message = 'Il server non ha confermato l’operazione. Verifica lo stato della scheda prima di riprovare.';
      }
    }
    throw error;
  }
  if (auth.currentUser !== user) throw Object.assign(new Error('La sessione è cambiata. Ricarica la scheda.'), { code: 'training/unauthenticated' });
  const result = response?.data;
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    throw Object.assign(new Error('La risposta del server non è completa. Verifica lo stato della scheda prima di riprovare.'), { code: 'training/acknowledgement' });
  }
  if (trainingWriteDiagnosticsEnabled) console.info('[training-write-performance]', JSON.stringify({ action, transport: 'server',
    elapsedMs: Math.round((performance.now() - startedAt) * 1000) / 1000, ...performanceDiagnostics(result.diagnostics) }));
  return { ...result, ...(result.savedAt ? { savedAt: new Date(result.savedAt) } : {}) };
}
